/*
 * Declarative shadow DOM is only initialized on the initial render of the page.
 * If the component is mounted after the browser finishes the initial render,
 * the shadow root needs to be manually hydrated.
 */
export class DeclarativeShadowElement extends HTMLElement {
  connectedCallback() {
    if (!this.shadowRoot) {
      const template = this.querySelector(':scope > template[shadowrootmode="open"]');
      if (!(template instanceof HTMLTemplateElement)) return;
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.append(template.content.cloneNode(true));
    }
  }
}

export class ResizeNotifier extends ResizeObserver {
  #initialized = false;
  constructor(callback) {
    super((entries) => {
      if (this.#initialized) return callback(entries, this);
      this.#initialized = true;
    });
  }
  disconnect() {
    this.#initialized = false;
    super.disconnect();
  }
}

export class OverflowMinimumEvent extends Event {
  constructor(minimumReached) {
    super('overflowMinimum', { bubbles: true });
    this.detail = { minimumReached };
  }
}

export class ReflowEvent extends Event {
  constructor(lastVisibleElement) {
    super('reflow', { bubbles: true });
    this.detail = { lastVisibleElement };
  }
}

export class OverflowList extends DeclarativeShadowElement {
  static get observedAttributes() {
    return ['disabled', 'minimum-items'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'disabled') {
      if (newValue === 'true') {
        this.#reset();
      } else {
        this.#reflowItems();
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.hasAttribute('defer')) {
      const deferredReflow = () => {
        this.removeAttribute('defer');
        this.#initialize();
      };
      const { schedule } = this;
      const requestIdleCallback =
        typeof window.requestIdleCallback === 'function' ? window.requestIdleCallback : setTimeout;
      requestIdleCallback(() => schedule(deferredReflow));
    } else if (this.shadowRoot) {
      this.#initialize();
    } else {
      setTimeout(() => { this.#initialize(); }, 0);
    }
  }

  #initialize() {
    const { shadowRoot } = this;
    if (!shadowRoot) throw new Error('Missing shadow root');
    const defaultSlot = shadowRoot.querySelector('slot:not([name])');
    const overflowSlot = shadowRoot.querySelector('slot[name="overflow"]');
    const moreSlot = shadowRoot.querySelector('slot[name="more"]');
    const overflow = shadowRoot.querySelector('[part="overflow"]');
    const list = shadowRoot.querySelector('[part="list"]');
    const placeholder = shadowRoot.querySelector('[part="placeholder"]');
    if (
      !(defaultSlot instanceof HTMLSlotElement) ||
      !(overflowSlot instanceof HTMLSlotElement) ||
      !(moreSlot instanceof HTMLSlotElement) ||
      !(overflow instanceof HTMLElement) ||
      !(list instanceof HTMLUListElement) ||
      !(placeholder instanceof HTMLLIElement)
    ) {
      throw new Error('Invalid element types in <OverflowList />');
    }
    this.#refs = { defaultSlot, overflowSlot, moreSlot, overflow, list, placeholder };
    this.addEventListener('reflow', (event) => {
      this.#reflowItems(event.detail.lastVisibleElement);
    });
    this.#reflowItems();
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
  }

  get schedule() {
    return typeof Theme?.utilities?.scheduler?.schedule === 'function'
      ? Theme.utilities.scheduler.schedule
      : (callback) => requestAnimationFrame(() => setTimeout(callback, 0));
  }

  #scheduled = false;

  get minimumItems() {
    const value = this.getAttribute('minimum-items');
    return value ? parseInt(value, 10) : null;
  }

  get overflowSlot() { return this.#refs.overflowSlot; }
  get defaultSlot() { return this.#refs.defaultSlot; }

  #lastDimensions = { width: null, height: null };

  #handleChange = (event) => {
    if (this.#scheduled) return;
    let width = null, height = null, isResize = false;
    for (const [, entry] of event.entries()) {
      if (!(entry instanceof ResizeObserverEntry)) break;
      isResize = true;
      width = Math.round(entry.contentRect.width);
      height = Math.round(entry.contentRect.height);
    }
    if (isResize) {
      if (!width || !height || (width === this.#lastDimensions.width && height === this.#lastDimensions.height)) return;
      this.#lastDimensions = { width: Math.round(width), height: Math.round(height) };
    }
    this.#scheduled = true;
    this.schedule(() => {
      this.#reflowItems();
      this.#scheduled = false;
    });
  };

  #moveItemsToDefaultSlot() {
    const { defaultSlot, overflowSlot } = this.#refs;
    for (const element of overflowSlot.assignedElements()) {
      element.slot = defaultSlot.name;
    }
  }

  #reset() {
    const { list } = this.#refs;
    this.#mutationObserver.disconnect();
    this.#resizeObserver.disconnect();
    this.#moveItemsToDefaultSlot();
    list.style.removeProperty('height');
    this.style.setProperty('--overflow-count', '0');
  }

  #updateMinimumReached(visibleElements) {
    if (this.minimumItems !== null) {
      const minimumReached = visibleElements.length < this.minimumItems;
      if (minimumReached) {
        this.setAttribute('minimum-reached', '');
      } else {
        this.removeAttribute('minimum-reached');
      }
      this.dispatchEvent(new OverflowMinimumEvent(minimumReached));
    }
  }

  showAll() {
    const { placeholder } = this.#refs;
    placeholder.style.setProperty('width', '0');
    placeholder.style.setProperty('display', 'none');
    this.setAttribute('disabled', 'true');
  }

  #reflowItems = (lastVisibleElement) => {
    const { defaultSlot, overflowSlot, moreSlot, list, placeholder } = this.#refs;
    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();
    const { height } = this.firstElementChild?.getBoundingClientRect() ?? {};
    if (height) list.style.height = `${height}px`;
    list.style.setProperty('overflow', 'hidden');
    this.#moveItemsToDefaultSlot();
    const elements = defaultSlot.assignedElements();
    moreSlot.hidden = true;
    placeholder.hidden = true;
    const rootRect = list.getBoundingClientRect();
    this.#lastDimensions = { width: Math.round(rootRect.width), height: Math.round(rootRect.height) };
    const getVisibleElements = () => elements.filter((el) => el.getBoundingClientRect().top <= rootRect.top);
    let visibleElements = getVisibleElements();
    if (visibleElements.length !== elements.length || lastVisibleElement) {
      moreSlot.style.setProperty('order', '-1');
      moreSlot.hidden = false;
      moreSlot.style.setProperty('height', `${height}px`);
      if (lastVisibleElement) lastVisibleElement.style.setProperty('order', '-1');
      visibleElements = getVisibleElements();
      moreSlot.style.removeProperty('order');
      if (lastVisibleElement) lastVisibleElement.style.removeProperty('order');
      if (lastVisibleElement) {
        const visibleIndex = visibleElements.indexOf(lastVisibleElement);
        if (visibleIndex !== -1) {
          visibleElements.splice(visibleIndex, 1);
          visibleElements.push(lastVisibleElement);
        }
      }
      moreSlot.style.setProperty('height', 'auto');
    }
    const overflowingElements = elements.filter((element) => !visibleElements.includes(element));
    const [firstOverflowingElement] = overflowingElements;
    const hasOverflow = overflowingElements.length > 0;
    const placeholderWidth = firstOverflowingElement ? firstOverflowingElement.clientWidth : 0;
    for (const element of elements) {
      element.slot = overflowingElements.includes(element) ? overflowSlot.name : defaultSlot.name;
    }
    moreSlot.hidden = !hasOverflow;
    if (hasOverflow) {
      placeholder.style.width = `${placeholderWidth}px`;
      placeholder.hidden = false;
    }
    list.style.setProperty('counter-reset', `overflow-count ${overflowingElements.length}`);
    this.style.setProperty('--overflow-count', `${overflowingElements.length}`);
    list.style.removeProperty('overflow');
    hasOverflow && this.#updateMinimumReached(visibleElements);
    this.#resizeObserver.observe(this);
    this.#mutationObserver.observe(this, { childList: true });
  };

  #refs;
  #resizeObserver = new ResizeNotifier(this.#handleChange);
  #mutationObserver = new MutationObserver(this.#handleChange);
}

if (!customElements.get('overflow-list')) {
  customElements.define('overflow-list', OverflowList);
}

export function calculateHeaderGroupHeight(
  header = document.querySelector('#header-component'),
  headerGroup = document.querySelector('#header-group')
) {
  if (!headerGroup) return 0;
  let totalHeight = 0;
  const children = headerGroup.children;
  for (let i = 0; i < children.length; i++) {
    const element = children[i];
    if (element === header || !(element instanceof HTMLElement)) continue;
    totalHeight += element.offsetHeight;
  }
  if (header instanceof HTMLElement && header.hasAttribute('transparent') && header.parentElement?.nextElementSibling) {
    return totalHeight + header.offsetHeight;
  }
  return totalHeight;
}

function updateHeaderHeights() {
  const header = document.querySelector('header-component');
  if (!(header instanceof HTMLElement)) return;
  const headerHeight = header.offsetHeight;
  const headerGroupHeight = calculateHeaderGroupHeight(header);
  document.body.style.setProperty('--header-height', `${headerHeight}px`);
  document.body.style.setProperty('--header-group-height', `${headerGroupHeight}px`);
}

function updateTransparentHeaderOffset() {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');
  const hasHeaderSection = headerGroup?.querySelector('.header-section');
  if (!hasHeaderSection || !header?.hasAttribute('transparent')) {
    document.body.style.setProperty('--transparent-header-offset-boolean', '0');
    return;
  }
  const hasImmediateSection = hasHeaderSection.nextElementSibling?.classList.contains('shopify-section');
  const shouldApplyOffset = !hasImmediateSection ? '1' : '0';
  document.body.style.setProperty('--transparent-header-offset-boolean', shouldApplyOffset);
}

export function updateAllHeaderCustomProperties() {
  updateHeaderHeights();
  updateTransparentHeaderOffset();
}

// Ejecutar al cargar
updateAllHeaderCustomProperties();
